import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { IssueViewItem } from "@shared-core/data/api-types";
import type {
  IssueViewListOptions,
  ListResponse,
} from "@shared-core/data/adapter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { Button } from "../app/components/primitives/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import {
  IssueStatusBadge,
  IssueTypeBadge,
  IssueSourceBadge,
} from "../app/components/domain/issues/issueBadges";
import { Plus } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import { formatCount } from "@shared-core/domain/formatters";
import {
  ISSUE_TYPE_LABELS,
  formatIssueAge,
  isIssueResolved,
} from "@shared-core/domain/issues";

const PAGE_SIZE = 25;

type StatusTab = "all" | "open" | "investigating" | "resolved";
type SourceFilter = "all" | "platform" | "legacy";
type SortOption = "newest" | "oldest" | "age";

const STATUS_TABS: { id: StatusTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "investigating", label: "Investigating" },
  { id: "resolved", label: "Resolved" },
];

export function IssuesPage() {
  const navigate = useNavigate();
  const adapter = useDataAdapter();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") ?? undefined;

  const [result, setResult] = useState<ListResponse<IssueViewItem> | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  // Initial status/type come from the URL so deep links from the work
  // surfaces (e.g. a job's "open issues" chip → ?status=open) land pre-filtered.
  const _initialStatus = searchParams.get("status");
  const [statusTab, setStatusTab] = useState<StatusTab>(
    _initialStatus === "open" ||
      _initialStatus === "investigating" ||
      _initialStatus === "resolved"
      ? _initialStatus
      : "all",
  );
  const [source, setSource] = useState<SourceFilter>("all");
  const [typeFilter, setTypeFilter] = useState<string>(
    searchParams.get("type") ?? "all",
  );
  const [sort, setSort] = useState<SortOption>("newest");

  const [customerName, setCustomerName] = useState<string | null>(null);
  useEffect(() => {
    if (!customerFilter) {
      setCustomerName(null);
      return;
    }
    adapter
      .getCustomerById(customerFilter)
      .then((c) => {
        if (c) setCustomerName(c.customer.name);
      })
      .catch(() => {});
  }, [adapter, customerFilter]);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset to page 1 when any filter changes.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab, source, typeFilter, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const options: IssueViewListOptions = {
      status: statusTab === "all" ? undefined : [statusTab],
      source: source === "all" ? undefined : source,
      typeId: typeFilter === "all" ? undefined : [typeFilter],
      customerId: customerFilter,
      sort:
        sort === "newest"
          ? "-raised_at"
          : sort === "oldest"
            ? "raised_at"
            : "-age_days",
      page,
      pageSize: PAGE_SIZE,
    };
    adapter
      .listIssues(options)
      .then((res) => {
        if (!cancelled) setResult(res);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, statusTab, source, typeFilter, sort, page, customerFilter]);

  const issues: IssueViewItem[] = result?.items ?? [];
  const total = result?.total ?? 0;
  const totalPages = result?.totalPages ?? 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <h1 className="mb-1">
            {customerFilter ? `Issues for ${customerName ?? "…"}` : "Issues"}
          </h1>
          {customerFilter ? (
            <CustomerScopeChip
              customerName={customerName}
              onDismiss={() => {
                const next = new URLSearchParams(searchParams);
                next.delete("customer");
                setSearchParams(next, { replace: true });
              }}
            />
          ) : (
            <p className="text-muted-foreground">
              Raise, investigate and resolve quality issues, complaints and
              improvements
            </p>
          )}
        </div>
        <Button
          onClick={() =>
            navigate(
              paths.raiseIssue(
                customerFilter ? { customerId: customerFilter } : undefined,
              ),
            )
          }
        >
          <Plus className="w-4 h-4" />
          Raise issue
        </Button>
      </div>

      {/* Toolbar: count + all filters in one row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-foreground whitespace-nowrap mr-1">
          {loading ? "—" : formatCount(total)} issues
        </span>

        <div className="w-px h-4 bg-border shrink-0" />

        {/* Status tabs */}
        {STATUS_TABS.map((tab) => {
          const active = statusTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatusTab(tab.id)}
              className={`px-3 py-1 rounded text-sm ${
                active
                  ? "bg-[color:var(--color-primary)] text-white"
                  : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label}
            </button>
          );
        })}

        <div className="w-px h-4 bg-border shrink-0" />

        {/* Source filter */}
        {(["all", "platform", "legacy"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSource(s)}
            className={`px-3 py-1 rounded text-sm capitalize ${
              source === s
                ? "bg-[color:var(--color-primary)] text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {s}
          </button>
        ))}

        <div className="w-px h-4 bg-border shrink-0" />

        {/* Type dropdown */}
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v)}>
          <SelectTrigger className="h-7 text-sm w-[160px]">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {Object.entries(ISSUE_TYPE_LABELS).map(([id, label]) => (
              <SelectItem key={id} value={id}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort dropdown */}
        <Select value={sort} onValueChange={(v) => setSort(v as SortOption)}>
          <SelectTrigger className="h-7 text-sm w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="age">Longest open</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Issue table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="text-muted-foreground">Loading issues…</div>
        </div>
      ) : issues.length === 0 ? (
        <div className="text-sm p-8 text-center border border-border rounded-lg space-y-1">
          <p className="font-medium text-foreground">
            No issues match these filters
          </p>
          <p className="text-muted-foreground">
            Try widening the status or source filter, or raise a new issue.
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[90px]">Ref</TableHead>
                <TableHead className="w-[110px]">Status</TableHead>
                <TableHead className="w-[120px]">Type</TableHead>
                <TableHead className="w-[90px]">Source</TableHead>
                <TableHead className="min-w-0">Title</TableHead>
                <TableHead className="w-[160px]">Customer</TableHead>
                <TableHead className="w-[80px]">Age</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {issues.map((issue) => {
                const resolved = isIssueResolved(issue);
                return (
                  <TableRow
                    key={issue.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(paths.issueDetail({ id: issue.id }))
                    }
                  >
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {issue.reference}
                    </TableCell>
                    <TableCell>
                      <IssueStatusBadge status={issue.status} />
                    </TableCell>
                    <TableCell>
                      <IssueTypeBadge type={issue.type_id} />
                    </TableCell>
                    <TableCell>
                      <IssueSourceBadge source={issue.source} />
                    </TableCell>
                    <TableCell className="font-medium max-w-0">
                      <span className="truncate block">{issue.title}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      <span className="line-clamp-1">
                        {issue.customer_name ?? <em>Internal</em>}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {resolved ? "—" : formatIssueAge(issue.age_days)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <ListPagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
